"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Upload = void 0;
const tslib_1 = require("tslib");
const client_utils_1 = require("@sensenet/client-utils");
const uuid_1 = require("uuid");
/**
 * Helper class for uploading content into the sensenet Repository
 */
class Upload {
    constructor(repository) {
        this.repository = repository;
    }
    /**
     * Uploads a specified text as a binary file
     * @param {UploadTextOptions} options The additional options
     */
    textAsFile(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const uploadFileOptions = Object.assign({ file: new File([options.text], options.fileName) }, options);
            return yield this.file(uploadFileOptions);
        });
    }
    /**
     * Uploads a specified file into a sensenet Repository
     * @param {UploadFileOptions} options The additional upload options
     */
    file(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.isChunkedUploadNeeded(options.file)) {
                return yield this.uploadChunked(options);
            }
            return yield this.uploadNonChunked(options);
        });
    }
    /**
     * Returns if a chunked upload is needed for a specified file
     * @param {File} file The File object
     */
    isChunkedUploadNeeded(file) {
        return file.size >= this.repository.configuration.chunkSize;
    }
    getUploadUrl(options) {
        return client_utils_1.PathHelper.joinPaths(this.repository.configuration.repositoryUrl, this.repository.configuration.oDataToken, client_utils_1.PathHelper.getContentUrl(options.parentPath), 'upload');
    }
    getFormDataFromOptions(options) {
        const formData = new FormData();
        formData.append('ChunkToken', '0*0*False*False');
        formData.append('FileName', options.fileName || options.file.name);
        formData.append('Overwrite', options.overwrite.toString());
        formData.append('PropertyName', options.binaryPropertyName.toString());
        formData.append('FileLength', options.file.size.toString());
        if (options.contentTypeName) {
            formData.append('ContentType', options.contentTypeName.toString());
        }
        return formData;
    }
    uploadNonChunked(options) {
        var _a, _b, _c;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const guid = uuid_1.v1();
            let responseContent = {};
            (_a = options.progressObservable) === null || _a === void 0 ? void 0 : _a.setValue({
                guid,
                file: options.file,
                completed: false,
            });
            const formData = this.getFormDataFromOptions(options);
            formData.append(options.file.name, options.file);
            try {
                const response = yield this.repository.fetch(this.getUploadUrl(options), Object.assign(Object.assign({}, options.requestInit), { credentials: 'include', method: 'POST', body: formData }));
                if (response.ok) {
                    responseContent = yield response.json();
                    (_b = options.progressObservable) === null || _b === void 0 ? void 0 : _b.setValue({
                        guid,
                        file: options.file,
                        chunkCount: 1,
                        uploadedChunks: 1,
                        completed: true,
                        createdContent: responseContent,
                    });
                }
                else {
                    throw yield this.repository.getErrorFromResponse(response);
                }
                return responseContent;
            }
            catch (error) {
                (_c = options.progressObservable) === null || _c === void 0 ? void 0 : _c.setValue({
                    guid,
                    file: options.file,
                    completed: false,
                    error,
                });
                throw error;
            }
        });
    }
    uploadChunked(options) {
        var _a, _b, _c;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const chunkCount = Math.floor(options.file.size / this.repository.configuration.chunkSize);
            const guid = uuid_1.v1();
            (_a = options.progressObservable) === null || _a === void 0 ? void 0 : _a.setValue({
                guid,
                file: options.file,
                completed: false,
                chunkCount,
                uploadedChunks: 0,
            });
            const uploadPath = this.getUploadUrl(options);
            /** initial chunk data and request */
            const formData = this.getFormDataFromOptions(options);
            formData.append(options.file.name, options.file.slice(0, this.repository.configuration.chunkSize));
            formData.append('UseChunk', 'true');
            formData.append('create', '1');
            try {
                const initRequest = yield this.repository.fetch(uploadPath, Object.assign(Object.assign({}, options.requestInit), { body: formData, credentials: 'include', method: 'POST', headers: {
                        'Content-Range': `bytes 0-${this.repository.configuration.chunkSize - 1}/${options.file.size}`,
                        'Content-Disposition': `attachment; filename="${options.file.name}"`,
                    } }));
                const chunkToken = yield initRequest.text();
                let lastResponseContent = {};
                for (let i = 0; i <= chunkCount; i++) {
                    const start = i * this.repository.configuration.chunkSize;
                    let end = start + this.repository.configuration.chunkSize;
                    end = end > options.file.size ? options.file.size : end;
                    const chunkFormData = new FormData();
                    const chunkData = options.file.slice(start, end);
                    chunkFormData.append('FileLength', options.file.size.toString());
                    chunkFormData.append('ChunkToken', chunkToken);
                    chunkFormData.append(options.file.name, chunkData);
                    const lastResponse = yield this.repository.fetch(uploadPath, Object.assign(Object.assign({}, options.requestInit), { body: chunkFormData, credentials: 'include', method: 'POST', headers: {
                            'Content-Range': `bytes ${start}-${end - 1}/${options.file.size}`,
                            'Content-Disposition': `attachment; filename="${options.file.name}"`,
                        } }));
                    if (lastResponse.ok) {
                        lastResponseContent = yield lastResponse.json();
                        (_b = options.progressObservable) === null || _b === void 0 ? void 0 : _b.setValue({
                            guid,
                            file: options.file,
                            chunkCount,
                            uploadedChunks: i,
                            completed: i === chunkCount,
                            createdContent: lastResponseContent,
                        });
                    }
                    else {
                        throw yield this.repository.getErrorFromResponse(lastResponse);
                    }
                }
                return lastResponseContent;
            }
            catch (error) {
                (_c = options.progressObservable) === null || _c === void 0 ? void 0 : _c.setValue({
                    guid,
                    file: options.file,
                    chunkCount,
                    uploadedChunks: 0,
                    completed: false,
                    error,
                });
                throw error;
            }
        });
    }
    webkitFileHandler(fileEntry, contentPath, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield new Promise((resolve, reject) => {
                fileEntry.file((f) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                    yield this.file(Object.assign(Object.assign({ file: f }, options), { parentPath: contentPath }));
                    resolve();
                }), (err) => reject(err));
            });
        });
    }
    webkitDirectoryHandler(directory, contentPath, options, readEntries = true) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const folder = yield this.repository.post({
                content: {
                    Name: directory.name,
                },
                parentPath: contentPath,
                contentType: 'Folder',
            });
            if (readEntries) {
                const dirReader = directory.createReader();
                yield new Promise((resolve, reject) => {
                    dirReader.readEntries((items) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                        yield this.webkitItemListHandler(items, folder.d.Path, true, options);
                        resolve();
                    }), (err) => reject(err));
                });
            }
        });
    }
    webkitItemListHandler(items, contentPath, createFolders, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            for (const item of items) {
                if (createFolders && item.isDirectory) {
                    yield this.webkitDirectoryHandler(item, contentPath, options);
                }
                if (item.isFile) {
                    yield this.webkitFileHandler(item, contentPath, options);
                }
            }
        });
    }
    /**
     * Uploads content from a specified Drop Event
     * @param { UploadOptions } options Options for the Upload request
     */
    fromDropEvent(options) {
        var _a;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (window.webkitRequestFileSystem) {
                const entries = options.event.dataTransfer
                    ? [].map.call(options.event.dataTransfer.items, (i) => i.webkitGetAsEntry())
                    : [];
                yield this.webkitItemListHandler(entries, options.parentPath, options.createFolders, options);
            }
            else {
                // Fallback for non-webkit browsers.
                ((_a = options.event.dataTransfer) === null || _a === void 0 ? void 0 : _a.files) &&
                    [].forEach.call(options.event.dataTransfer.files, (f) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                        if (f.type === 'file') {
                            return yield this.file(Object.assign({ file: f }, options));
                        }
                    }));
            }
        });
    }
    /**
     * Uploads files (and optionally creates the directory structure) from a file list
     * @param { UploadFromFileListOptions } options Options for the Upload request
     */
    fromFileList(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (options.createFolders) {
                const directories = new Set(Array.from(options.fileList).map((f) => client_utils_1.PathHelper.getParentPath(f.webkitRelativePath || f.fullPath || '')));
                const directoriesBySegments = Array.from(directories).map((d) => client_utils_1.PathHelper.getSegments(d));
                const createdDirectories = new Set();
                for (const directory of directoriesBySegments) {
                    let currentPath = options.parentPath;
                    for (const segment of directory) {
                        const pathToCreate = client_utils_1.PathHelper.joinPaths(currentPath, segment);
                        if (!createdDirectories.has(pathToCreate)) {
                            yield this.webkitDirectoryHandler({ name: segment }, currentPath, options, false);
                        }
                        createdDirectories.add(pathToCreate);
                        currentPath = pathToCreate;
                    }
                }
                yield Promise.all(Array.from(options.fileList).map((file) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                    yield this.file(Object.assign(Object.assign({}, options), { parentPath: client_utils_1.PathHelper.joinPaths(options.parentPath, client_utils_1.PathHelper.getParentPath(file.webkitRelativePath)), file }));
                })));
            }
            else {
                const { fileList, createFolders } = options, uploadOptions = tslib_1.__rest(options, ["fileList", "createFolders"]);
                for (const file of Array.from(options.fileList)) {
                    yield this.file(Object.assign(Object.assign({}, uploadOptions), { file }));
                }
            }
        });
    }
}
exports.Upload = Upload;
//# sourceMappingURL=Upload.js.map